<template>
  <div>
    <div class="title">
      {{ _['upload_' + upload.model] }}
      <span @click="closeModal()"><font-awesome-icon :icon="['fas', 'times']" /></span>
    </div>
    <div class="content">
      <div class="upload">
        <div class="template" @click="getTemplate()">
          <p>{{ _['download_template_' + upload.model] }}</p>
        </div>
        <div class="drop" :class="{ over: over, processing: processing }" @click="$refs.file.click()" @dragenter="over = true" @dragover="over = true" @dragleave="over = false" @drop="dropFile($event)">
          <span><font-awesome-icon :icon="['fas', 'arrow-alt-circle-up']" /></span>
          <p>{{ _['click_drop_' + upload.model] }}</p>
          <input type="file" accept=".xlsx, .xls" ref="file" @change="selectFile">
        </div>
      </div>
      <div class="errors" v-if="!processing && Object.keys(errors).length">
        <b style="color: red">Errors<span v-if="row"> in row {{ row }}</span>:</b>
        <div v-if="Array.isArray(errors)">
          <span v-for="(error, ekey) in errors" :key="ekey">
            {{ error }}
          </span>
        </div>
        <div v-else>
          <div v-for="(column, ckey) in errors" :key="ckey">
            <b style="display: inline-block; margin-top: 4px">{{ ckey }}</b><br>
            <span v-for="(error, ekey) in column" :key="ekey">
              {{ error }}<br>
            </span>
          </div>
        </div>
      </div>
      <div class="success" v-if="success">
        <b style="color: green">{{ success }}</b>
      </div>
    </div>
  </div>
</template>

<script>
import { API_URL } from '@/config'
import { mapGetters, mapMutations } from 'vuex'
export default {
  data() {
    return {
      over: false,
      file: null,
      processing: false,
      row: null,
      errors: {},
      options: {},
      success: null,
      imported: {},
    }
  },
  computed: {
    ...mapGetters(['upload']),
  },
  methods: {
    ...mapMutations(['closeModal']),
    async getTemplate() {
      if (this.progress) return;
      let report = await this.axios.get(API_URL + '/upload/template?model=' + this.upload.model, {
        responseType: 'blob',
      });
      this.setProgress(false);
      const href = window.URL.createObjectURL(new Blob([report.data]));
      const link = document.createElement('a');
      link.href = href;
      link.setAttribute('download', this.upload.model + '.xlsx');
      document.body.appendChild(link);
      link.click();
    },
    dropFile(event) {
      if (this.progress) return;
      this.over = false;
      const transfer = event.dataTransfer;
      this.$refs.file.files = transfer.files;
      this.selectFile();
    },
    selectFile() {
      if (this.progress) return;
      this.file = this.$refs.file.files[0];
      if (!this.validateFile()) return;
      this.uploadFile();
    },
    validateFile() {
      this.errors = [];
      if (![
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel'
      ].includes(this.file.type)) {
        this.errors.push('Invalid file type (.xlsx, .xls).');
      }
      if (this.file.size > 5242880) {
        this.errors.push('Invalid file size (5 mb max).');
      }
      if (this.errors.length) {
        setTimeout(() => this.errors = [], 2000);
      }
      return !this.errors.length;
    },
    async uploadFile() {

      if (this.progress || this.processing) return;
      this.processing = true;
      const form = new FormData();
      const headers = { 'Content-Type': 'multipart/form-data' };

      try {

        form.append('file', this.file);
        const response = await this.axios.post(API_URL + '/upload?model=' + this.upload.model, form, { headers });
        this.success = response.data.success;
        this.imported = response.data.imported;
        this.processing = false;
        this.setProgress(false);
        this.$store.commit(this.upload.model + '/setImported', response.data.imported, { root: true });

      } catch (error) {

        if (error.response.data.row) this.row = error.response.data.row;
        this.errors = error.response.data.errors;
        if (error.response.data.options) this.options = error.response.data.options;
        this.processing = false;
        this.setProgress(false);
        return;
      }
    },
    preventDefaults(event) {
      event.preventDefault();
      event.stopPropagation();
    },
  },
  created() {
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(event => {
      document.body.addEventListener(event, this.preventDefaults, false);
    });
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/variables.scss';
.modal {
  > div {
    > div {
      display: grid;
      grid-template-rows: 40px 1fr;
      grid-template-columns: 1fr;
      height: 100%;
      > .title {
        grid-area: 1/1/2/2;
        background: $primary-color;
        color: #ffffff;
        padding: 12px;
        font-weight: bold;
        > span {
          float: right;
          margin-right: 4px;
          opacity: .4;
          &:hover {
            opacity: 1;
          }
        }
        .svg-inline--fa {
          cursor: pointer;
        }
      }
      > .content {
        grid-area: 2/1/3/2;
        background: #ffffff;
        overflow-y: scroll;
        position: relative;
        > div {
          &.upload {
            > div {
              margin: 6px;
              padding: 5px;
              height: 106px;
              display: flex;
              flex-flow: column;
              justify-content: center;
              align-items: center;
              background: lighten($primary-color, 90%);
              border: 1px solid lighten($primary-color, 80%);
              border-radius: 4px;
              user-select: none;
              &.template {
                cursor: pointer;
                &:hover {
                  background: lighten($primary-color, 85%);
                }
                > span {
                  font-size: 4rem;
                  color: lighten($primary-color, 70%);
                }
              }
              &.drop {
                height: 268px;
                background: lighten($primary-color, 97%);
                border: 1px dashed lighten($primary-color, 80%);
                &.over {
                  background: lighten($primary-color, 93%);
                  > span {
                    color: lighten($primary-color, 20%);
                  }
                }
                > span {
                  font-size: 4rem;
                  color: lighten($primary-color, 80%);
                }
                > input {
                  display: none;
                }

                &.processing {
                  background-image: -webkit-linear-gradient(
                    -45deg,
                    lighten($primary-color, 97%) 25%,
                    lighten($primary-color, 93%) 25%,
                    lighten($primary-color, 93%) 50%,
                    lighten($primary-color, 97%) 50%,
                    lighten($primary-color, 97%) 75%,
                    lighten($primary-color, 93%) 75%,
                    lighten($primary-color, 93%)
                  );
                  background-size: 16px 16px;
                  animation: processing .5s linear 0s infinite;
                }

                @keyframes processing {
                  0% { background-position: 0 0; }
                  100% { background-position: 16px 16px; }
                }
              }
            }
          }
        }
        .errors {
          margin: 6px;
          padding: 11px 5px;
          background: #ffd4d4;
          border: 1px solid lighten(red, 85%);
          border-radius: 4px;
        }
        .success {
          text-align: center;
          margin: 6px;
          padding: 11px 5px;
          background: #f4fff4;
          border: 1px solid lighten(green, 85%);
          border-radius: 4px;
        }
      }
    }
  }
}

</style>